import { useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { withEnv } from "@/lib/three";

const boardGeo = (() => {
  const w = 5.2, h = 0.98, r = 0.1;
  const s = new THREE.Shape();
  s.moveTo(-w / 2 + r, -h / 2); s.lineTo(w / 2 - r, -h / 2); s.quadraticCurveTo(w / 2, -h / 2, w / 2, -h / 2 + r);
  s.lineTo(w / 2, h / 2 - r); s.quadraticCurveTo(w / 2, h / 2, w / 2 - r, h / 2); s.lineTo(-w / 2 + r, h / 2);
  s.quadraticCurveTo(-w / 2, h / 2, -w / 2, h / 2 - r); s.lineTo(-w / 2, -h / 2 + r); s.quadraticCurveTo(-w / 2, -h / 2, -w / 2 + r, -h / 2);
  const g = new THREE.ExtrudeGeometry(s, { depth: 0.16, bevelEnabled: true, bevelThickness: 0.04, bevelSize: 0.04, bevelSegments: 4, curveSegments: 8 });
  g.rotateX(-Math.PI / 2);
  g.translate(0, -0.08, 0);
  return g;
})();

const makeGrain = ({ base, line, density, knots, seed, wave }) => {
  const c = document.createElement("canvas");
  c.width = 1024; c.height = 256;
  const g = c.getContext("2d");
  g.fillStyle = base; g.fillRect(0, 0, 1024, 256);
  let s = seed;
  const rnd = () => (s = (s * 16807) % 2147483647) / 2147483647;
  g.strokeStyle = line;
  for (let i = 0; i < density; i++) {
    const y = rnd() * 256, amp = rnd() * wave, ph = rnd() * 6;
    g.globalAlpha = 0.06 + rnd() * 0.22; g.lineWidth = 0.5 + rnd() * 2;
    g.beginPath(); g.moveTo(0, y);
    for (let x = 0; x <= 1024; x += 32) g.lineTo(x, y + Math.sin(x * 0.006 + ph) * amp);
    g.stroke();
  }
  for (let k = 0; k < knots; k++) {
    const kx = 80 + rnd() * 860, ky = 40 + rnd() * 176;
    for (let rr = 3; rr < 24; rr += 3) { g.globalAlpha = 0.2; g.beginPath(); g.ellipse(kx, ky, rr * 2.4, rr * 0.55, 0, 0, Math.PI * 2); g.stroke(); }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(1 / 5.2, 1 / 0.98);
  t.offset.set(0.5, 0.5);
  return t;
};

export const MATERIALS = [
  { id: "composite", name: "Composite", grain: { base: "#7A7A7A", line: "#3A3A3A", density: 160, knots: 0, seed: 7, wave: 1 }, rough: 0.55 },
  { id: "hardwood", name: "Hardwood", grain: { base: "#4A4A4A", line: "#151515", density: 260, knots: 2, seed: 19, wave: 5 }, rough: 0.35 },
  { id: "cedar", name: "Cedar", grain: { base: "#B8B8B8", line: "#5A5A5A", density: 140, knots: 4, seed: 31, wave: 9 }, rough: 0.8 },
  { id: "pvc", name: "PVC", grain: { base: "#E4E4E4", line: "#9A9A9A", density: 70, knots: 0, seed: 43, wave: 0.5 }, rough: 0.45 },
];

const Boards = ({ kind, dragRef, progress }) => {
  const g = useRef();
  const boards = useRef([]);
  const flipAt = useRef(-10);
  const textures = useMemo(() => MATERIALS.map((m) => makeGrain(m.grain)), []);
  const mats = useMemo(() => MATERIALS.map((m, i) => new THREE.MeshStandardMaterial({ map: textures[i], roughness: m.rough })), [textures]);
  const prev = useRef(kind);
  const clock = useRef(0);

  useEffect(() => { if (prev.current !== kind) { flipAt.current = clock.current; prev.current = kind; } }, [kind]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    clock.current = t;
    boards.current.forEach((b, i) => {
      if (!b) return;
      const p = Math.min(Math.max((t - flipAt.current - i * 0.08) / 0.7, 0), 1);
      b.rotation.x = (1 - Math.pow(1 - p, 3)) * Math.PI * 2;
      b.position.y = Math.sin(p * Math.PI) * 0.6;
      b.material = mats[kind];
    });
    const ty = 0.6 + (dragRef.current || 0) + Math.sin(t * 0.3) * 0.15 + (progress?.get() || 0) * 1.2;
    g.current.rotation.y = THREE.MathUtils.lerp(g.current.rotation.y, ty, 0.06);
    g.current.position.y = Math.sin(t * 0.8) * 0.08;
  });

  return (
    <group ref={g} rotation={[0.35, 0.6, 0]}>
      {[-1.06, 0, 1.06].map((z, i) => (
        <mesh key={z} ref={(el) => { boards.current[i] = el; }} position={[0, 0, z]} castShadow receiveShadow geometry={boardGeo} material={mats[kind]} />
      ))}
      {[-1.8, 1.8].map((x) => (
        <mesh key={x} position={[x, -0.32, 0]} castShadow><boxGeometry args={[0.18, 0.38, 3.4]} /><meshStandardMaterial color="#1E1E1E" roughness={0.9} /></mesh>
      ))}
    </group>
  );
};

export const MaterialScene = ({ kind, dragRef, progress, className = "", testId }) => (
  <div className={className} data-testid={testId}>
    <Canvas shadows dpr={[1, 1.75]} camera={{ position: [0, 3.2, 7.5], fov: 38 }} onCreated={(st) => { st.camera.lookAt(0, 0, 0); withEnv(0.45)(st); }}>
      <ambientLight intensity={0.5} />
      <spotLight position={[3, 8, 5]} angle={0.6} penumbra={1} intensity={160} castShadow />
      <directionalLight position={[-5, 2, -3]} intensity={0.8} />
      <Boards kind={kind} dragRef={dragRef} progress={progress} />
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.4, 0]} receiveShadow>
        <planeGeometry args={[30, 30]} />
        <shadowMaterial opacity={0.4} />
      </mesh>
    </Canvas>
  </div>
);

export default MaterialScene;
